// useInlineDdlEditContext — wires the Compact DDL editor's edit-context hook
// to the DSE2 DDL-edit pipeline for the QueryEditor tab's Monaco buffer. Mirrors
// `useInlineToolbarEditContext` (DML / VQE-Edit) but returns a
// `VqeDdlEditContext` (DDL classification + DDL undo source tag).
//
// The hook produces a `model: MonacoTextModelLike` adapter that splices
// edits through the Monaco editor handle's `setValue` (the same shape
// VQE-Edit uses); commits funnel through `commitDdlEdit` which handles
// the `'vqe-ddl-edit'` undo tag + threshold gate. Service identity is
// keyed on `(connectionId, engine)` so swapping connections disposes the
// old service before the new buffer parses.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice A1 — Variant 1 (Query Editor inline).
import { useEffect, useMemo, useRef, useState } from 'react';

import { subscribe as subscribeToSchemaBus } from '../../../intellisense/schemaBus/schemaBus';
import { buildParsedDocumentFromSource } from '../../../sql-grammar/document';
import type {
  ParseOptions,
  ParsedDocument,
  SqlEngine,
} from '../../../sql-grammar/model';
import {
  createResolverService,
  type SqlResolverService,
} from '../../../sql-resolver/service/resolverService';
import { buildLivePullSchemaSnapshot } from '../../visual-query-editor/edit/livePullSchemaSnapshot';
import { createInlineModelAdapter } from '../../visual-query-editor/inline/inlineModelAdapter';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type {
  BlastRadiusGate,
  MonacoTextModelLike,
  VqeDdlPendingEdit,
} from '../edit/commitDdlEdit';
import type { DdlObjectKind, VqeDdlEditContext } from '../edit/editContext';
import { useVqeDdlStore } from '../store';

const DOC_PREFIX = 'inline-ddl:';

function buildSync(source: string, options: ParseOptions): ParsedDocument {
  return buildParsedDocumentFromSource(source, options);
}

export type UseInlineDdlEditContextArgs = {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly tabId: string;
  readonly query: string;
  readonly statementId: string | null;
  readonly options: ParseOptions;
  readonly connectionId?: string | null;
  readonly database?: string | null;
  /** Optional pending-edit setter — staged when threshold gate fires. */
  readonly setPendingEdit?: (pending: VqeDdlPendingEdit) => void;
  /** Slice H3 pre-destructive guard wiring. Inline hosts that have a live
   *  dependency snapshot for the parked object pass it here; commit then
   *  routes drops through the BlastRadiusPanel before applying. */
  readonly blastRadius?: BlastRadiusGate;
};

export function useInlineDdlEditContext(
  args: UseInlineDdlEditContextArgs,
): VqeDdlEditContext | null {
  const {
    editorRef,
    tabId,
    query,
    statementId,
    options,
    connectionId,
    database,
    setPendingEdit,
    blastRadius,
  } = args;
  const documentId = `${DOC_PREFIX}${tabId}`;

  const serviceConnectionId = connectionId ?? `inline-ddl-${tabId}`;
  const engine: SqlEngine = options.engine;

  const serviceRef = useRef<SqlResolverService | null>(null);
  const serviceKeyRef = useRef<string | null>(null);
  const [, forceUpdate] = useState(0);

  const desiredKey = `${serviceConnectionId}|${engine}`;
  if (serviceKeyRef.current !== desiredKey) {
    if (serviceRef.current) {
      try {
        serviceRef.current.onDisconnect();
      } catch {
        /* idempotent dispose */
      }
    }
    const pullSchemaSnapshot = connectionId
      ? buildLivePullSchemaSnapshot(connectionId, engine)
      : undefined;
    serviceRef.current = createResolverService({
      connectionId: serviceConnectionId,
      engineId: engine,
      buildParsedDocument: buildSync,
      pullSchemaSnapshot,
    });
    serviceKeyRef.current = desiredKey;
  }
  const service = serviceRef.current!;

  const openedRef = useRef<string | null>(null);
  const disposedRef = useRef(false);
  useEffect(() => {
    if (disposedRef.current) return;
    let cancelled = false;
    const onResolved = () => {
      if (!cancelled && !disposedRef.current) forceUpdate((n) => n + 1);
    };
    const swallowDisposed = (err: unknown) => {
      if (err instanceof Error && err.message.includes('service has been disposed')) return;
      throw err;
    };
    if (openedRef.current === null || openedRef.current !== documentId) {
      openedRef.current = documentId;
      void service
        .onOpenDocument({ documentId, text: query, options })
        .then(onResolved, swallowDisposed);
    } else {
      void service.onTextChange(documentId, query).then(onResolved, swallowDisposed);
    }
    return () => {
      cancelled = true;
    };
  }, [documentId, query, options, service]);

  useEffect(() => {
    if (!connectionId || !database) return undefined;
    let cancelled = false;
    const refresh = (): void => {
      void service.refreshSchemaSnapshot(database).then(
        () => {
          if (!cancelled && !disposedRef.current) forceUpdate((n) => n + 1);
        },
        () => undefined,
      );
    };
    refresh();
    const unsubscribe = subscribeToSchemaBus(connectionId, (event) => {
      if (cancelled || disposedRef.current) return;
      if (event.type === 'updated' && event.database !== database) return;
      if (event.type === 'updated' || event.type === 'invalidated') {
        refresh();
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [service, connectionId, database]);

  useEffect(() => {
    return () => {
      disposedRef.current = true;
      service.onDisconnect();
    };
  }, [service]);

  const model = useMemo<MonacoTextModelLike>(
    () =>
      createInlineModelAdapter(editorRef, () =>
        service.getParsedBuffer(documentId)?.parsedDocument.source ?? query,
      ),
    [editorRef, service, documentId, query],
  );

  const objectKind = useMemo<DdlObjectKind | null>(() => {
    if (!statementId) return null;
    try {
      const structure = service.extractDdlStructure(documentId, statementId);
      return ddlStructureKindToObjectKind(structure.kind);
    } catch {
      return null;
    }
  }, [service, documentId, statementId]);

  if (!statementId) return null;

  // Default the pending-edit setter to the global vqeDdlStore so every edit
  // funnels through the app-shell PreviewDialog (always-on preview) without
  // each host wiring it explicitly. See useTabDdlEditContext for the same
  // default.
  const resolvedSetPendingEdit =
    setPendingEdit ?? useVqeDdlStore.getState().setPendingEdit;

  return {
    service,
    documentId,
    statementId,
    model,
    setPendingEdit: resolvedSetPendingEdit,
    objectKind,
    connectionId: connectionId ?? null,
    database: database ?? null,
    blastRadius,
  };
}

function ddlStructureKindToObjectKind(
  kind:
    | 'create-table'
    | 'alter-table'
    | 'create-index'
    | 'drop-index'
    | 'create-type'
    | 'create-domain'
    | 'create-trigger'
    | 'alter-trigger'
    | 'drop-trigger'
    | 'create-routine'
    | 'alter-routine'
    | 'drop-routine'
    | 'grant'
    | 'revoke',
): DdlObjectKind {
  switch (kind) {
    case 'create-table':
    case 'alter-table':
      return 'table';
    case 'create-index':
      return 'index';
    case 'drop-index':
      return 'drop';
    case 'create-type':
      // Matches classifyDdlAtCursor's CREATE TYPE → 'type' mapping.
      return 'type';
    case 'create-domain':
      // Matches classifyDdlAtCursor's CREATE DOMAIN → 'domain' mapping.
      return 'domain';
    case 'create-trigger':
    case 'alter-trigger':
    case 'drop-trigger':
      // Matches classifyDdlAtCursor's CREATE/ALTER TRIGGER → 'trigger' mapping.
      return 'trigger';
    case 'create-routine':
    case 'alter-routine':
    case 'drop-routine':
      // Matches classifyDdlAtCursor's CREATE/ALTER {PROCEDURE|FUNCTION} →
      // 'routine' mapping.
      return 'routine';
    case 'grant':
    case 'revoke':
      // Seed-only GRANT/REVOKE shares the security surface (matches
      // classifyDdlAtCursor's GRANT/REVOKE → 'security' mapping).
      return 'security';
  }
}
